import { useEffect, useState } from 'react';
import { api, json } from '../api';
import { EmailVerification } from './EmailVerification';
import { supportCategoryLabels } from '../../shared/support';
export const supportLabels: Record<string, string> = {
  open: '待处理',
  processing: '处理中',
  resolved: '已解决',
  ...supportCategoryLabels,
};
export type SupportTicket = {
  id: string;
  body: string;
  category: string;
  status: string;
  game_name: string;
  created_at: string;
  messages?: { id: string; author: string; body: string; created_at: string; image?: string }[];
};
export function SupportConversation({
  id,
  admin = false,
  onUpdate,
  statusOverride,
}: {
  id: string;
  admin?: boolean;
  onUpdate?: () => void;
  statusOverride?: string;
}) {
  const [ticket, setTicket] = useState<SupportTicket | null>(null),
    [body, setBody] = useState(''),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false),
    [verified, setVerified] = useState(admin);
  const path = (admin ? '/admin/support/' : '/support/') + encodeURIComponent(id);
  useEffect(() => {
    let active = true;
    setTicket(null);
    setBody('');
    setError('');
    async function load() {
      try {
        const t = await api<SupportTicket>(path);
        if (!active) return;
        setTicket(t);
        if (!admin) await api(path + '/read', json('POST', { through: t.messages?.at(-1)?.id }));
      } catch (e) {
        if (active) setError((e as Error).message);
      }
    }
    void load();
    const timer = setInterval(() => {
      if (!document.hidden) void load();
    }, 15000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [path, admin]);
  useEffect(() => {
    if (admin) return;
    let active = true;
    const check = () => {
      void api<{ verified: boolean }>('/email-verification')
        .then((s) => {
          if (active) setVerified(s.verified);
        })
        .catch(() => {});
    };
    check();
    window.addEventListener('focus', check);
    return () => {
      active = false;
      window.removeEventListener('focus', check);
    };
  }, [admin]);
  return (
    <div className="support-conversation">
      {ticket && (
        <>
          <div className="support-conversation-heading">
            <strong>{ticket.game_name || supportLabels[ticket.category]}</strong>
            <span className={'support-state ' + (statusOverride || ticket.status)}>
              {supportLabels[statusOverride || ticket.status]}
            </span>
          </div>
          <small>反馈编号 {ticket.id.slice(0, 8).toUpperCase()}</small>
          <div className="support-message">
            <b>用户提交</b>
            <time>{new Date(ticket.created_at).toLocaleString()}</time>
            <p>{ticket.body}</p>
          </div>
          {ticket.messages?.map((m) => (
            <div key={m.id} className={'support-message ' + m.author}>
              <b>{m.author === 'admin' ? '摸鱼岛 · 管理员' : '用户补充'}</b>
              <time>{new Date(m.created_at).toLocaleString()}</time>
              <p>{m.body}</p>
              {m.image && (
                <a href={'/api' + path + '/images/' + m.image} target="_blank" rel="noreferrer">
                  <img
                    className="support-image"
                    src={'/api' + path + '/images/' + m.image}
                    alt="反馈截图"
                  />
                </a>
              )}
            </div>
          ))}
          {!admin && !verified && (
            <>
              <p>请先验证注册邮箱，再补充反馈。</p>
              <EmailVerification compact />
            </>
          )}
          <label className="support-upload">
            添加截图 <small>每张不超过 5 MB</small>
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              disabled={busy || !verified}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (!file) return;
                if (file.size > 5 * 1024 * 1024) {
                  setError('截图不能超过5 MB');
                  return;
                }
                setBusy(true);
                setError('');
                try {
                  const form = new FormData();
                  form.append('image', file);
                  await api(path + '/images', { method: 'POST', body: form });
                  setTicket(await api<SupportTicket>(path));
                  onUpdate?.();
                } catch (e) {
                  setError((e as Error).message);
                } finally {
                  setBusy(false);
                }
              }}
            />
          </label>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (busy) return;
              setBusy(true);
              setError('');
              try {
                await api(path + '/messages', json('POST', { body }));
                setBody('');
                setTicket(await api<SupportTicket>(path));
                onUpdate?.();
              } catch (e) {
                setError((e as Error).message);
              } finally {
                setBusy(false);
              }
            }}
          >
            <label>
              {admin ? '回复用户（用户可见）' : '继续补充'}
              <textarea
                rows={3}
                maxLength={2000}
                required
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder={
                  admin
                    ? '说明处理进展，或询问需要补充的信息…'
                    : '补充问题详情，或告诉我们是否已经解决…'
                }
              />
            </label>
            <button className="button" disabled={busy || !verified || !body.trim()}>
              {busy ? '发送中…' : admin ? '发送回复' : '发送补充'}
            </button>
          </form>
        </>
      )}
      {error && <p role="alert">{error}</p>}
      {!ticket && !error && <p>正在读取反馈…</p>}
    </div>
  );
}
